import React from 'react'
import { PageShell } from './PageShell'
import { apiGet, apiPost, apiPut, apiDelete } from '../../../lib/api'
import { SERVICES, SERVICE } from '../../../lib/endpoints'
import type { Service, OfferingType } from '../../../types'
import { ServiceImportWizard } from '../components/ServiceImportWizard'

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text)',
  marginBottom: 6,
  display: 'block',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--surface2)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '9px 12px',
  fontSize: 14,
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
}

const hintStyle: React.CSSProperties = { fontSize: 12, color: 'var(--text2)', marginTop: 4 }

// ─── Service Modal ─────────────────────────────────────────────────────────────

type ServiceFormValues = {
  name: string
  description: string
  price: string
  durationMinutes: string
  offeringType: OfferingType
}

interface ServiceModalProps {
  mode: 'add' | 'edit'
  initial?: Service | null
  onClose: () => void
  onSaved: () => void
}

function ServiceModal({ mode, initial, onClose, onSaved }: ServiceModalProps) {
  const [form, setForm] = React.useState<ServiceFormValues>({
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    price: initial?.price != null ? String(initial.price) : '',
    durationMinutes: initial?.durationMinutes != null ? String(initial.durationMinutes) : '',
    offeringType: initial?.offeringType ?? 'SERVICE',
  })
  const isProduct = form.offeringType === 'PRODUCT'
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function handleSubmit() {
    if (!form.name.trim()) { setError('Name is required.'); return }
    setBusy(true)
    setError(null)
    const body: Record<string, unknown> = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      price: form.price.trim() ? Number(form.price) : null,
      // Duration is only meaningful for services. Force-null on products.
      durationMinutes: isProduct
        ? null
        : (form.durationMinutes.trim() ? Number(form.durationMinutes) : null),
      offeringType: form.offeringType,
    }
    try {
      if (mode === 'add') {
        await apiPost<Service, Record<string, unknown>>(SERVICES, body)
      } else if (initial) {
        await apiPut<Service, Record<string, unknown>>(SERVICE(initial.id), {
          ...body,
          active: initial.active,
        })
      }
      onSaved()
      onClose()
    } catch (e) {
      setError((e as { message?: string }).message ?? 'Failed to save offering')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 95,
        display: 'grid',
        placeItems: 'center',
        padding: 24,
      }}
      onClick={() => { if (busy) return; onClose() }}
    >
      <div
        className="panel"
        style={{ width: 'min(520px, 96vw)', padding: 24 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontFamily: 'Manrope, sans-serif', fontSize: 22 }}>
            {mode === 'add' ? 'Add Offering' : 'Edit Offering'}
          </div>
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>

        <form onSubmit={e => { e.preventDefault(); void handleSubmit() }}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Type</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['SERVICE', 'PRODUCT'] as const).map(opt => {
                const active = form.offeringType === opt
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setForm(v => ({ ...v, offeringType: opt }))}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: active ? '1.5px solid var(--orange)' : '1px solid var(--border)',
                      background: active ? 'var(--orange-bg)' : 'var(--surface2)',
                      color: active ? 'var(--orange)' : 'var(--text)',
                      fontSize: 13,
                      fontWeight: active ? 600 : 500,
                      cursor: 'pointer',
                    }}
                  >
                    {opt === 'SERVICE' ? 'Service' : 'Product'}
                  </button>
                )
              })}
            </div>
            <div style={hintStyle}>
              {isProduct
                ? 'A saleable good (e.g. take-home shampoo). Duration is hidden.'
                : 'Something delivered by appointment (e.g. a haircut).'}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Name *</label>
            <input
              style={inputStyle}
              value={form.name}
              onChange={e => setForm(v => ({ ...v, name: e.target.value }))}
              placeholder={isProduct ? 'e.g. Take-home conditioner' : 'e.g. Haircut & Style'}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Description</label>
            <textarea
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
              value={form.description}
              onChange={e => setForm(v => ({ ...v, description: e.target.value }))}
              placeholder="Optional description (what it includes, who it's for...)"
            />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isProduct ? '1fr' : '1fr 1fr',
              gap: 16,
              marginBottom: 20,
            }}
          >
            <div>
              <label style={labelStyle}>Price</label>
              <input
                type="number"
                step="0.01"
                min="0"
                style={inputStyle}
                value={form.price}
                onChange={e => setForm(v => ({ ...v, price: e.target.value }))}
                placeholder="e.g. 45.00"
              />
              <div style={hintStyle}>Leave blank to hide price</div>
            </div>
            {!isProduct && (
              <div>
                <label style={labelStyle}>Duration (minutes)</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  style={inputStyle}
                  value={form.durationMinutes}
                  onChange={e => setForm(v => ({ ...v, durationMinutes: e.target.value }))}
                  placeholder="e.g. 60"
                />
                <div style={hintStyle}>Leave blank if variable</div>
              </div>
            )}
          </div>

          {error && (
            <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : mode === 'add' ? 'Add Offering' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function ServicesPage({ isActive }: { isActive: boolean }) {
  const [services, setServices] = React.useState<Service[]>([])
  const [loading, setLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [showAddModal, setShowAddModal] = React.useState(false)
  const [editTarget, setEditTarget] = React.useState<Service | null>(null)
  const [togglingId, setTogglingId] = React.useState<string | null>(null)
  const [deletingId, setDeletingId] = React.useState<string | null>(null)
  const [showImportWizard, setShowImportWizard] = React.useState(false)

  function loadServices() {
    setLoading(true)
    setLoadError(null)
    apiGet<Service[]>(SERVICES)
      .then(data => { setServices(data) })
      .catch(e => { setLoadError((e as { message?: string }).message ?? 'Failed to load offerings') })
      .finally(() => setLoading(false))
  }

  React.useEffect(() => {
    loadServices()
  }, [])

  async function handleToggleActive(svc: Service) {
    setTogglingId(svc.id)
    try {
      await apiPut<Service, Record<string, unknown>>(SERVICE(svc.id), {
        name: svc.name,
        description: svc.description ?? null,
        price: svc.price ?? null,
        durationMinutes: svc.durationMinutes ?? null,
        active: !svc.active,
      })
      loadServices()
    } catch (e) {
      window.alert((e as { message?: string }).message ?? 'Failed to update offering')
    } finally {
      setTogglingId(null)
    }
  }

  async function handleDelete(svc: Service) {
    if (!window.confirm(`Delete "${svc.name}"? This cannot be undone.`)) return
    setDeletingId(svc.id)
    try {
      await apiDelete(SERVICE(svc.id))
      loadServices()
    } catch (e) {
      window.alert((e as { message?: string }).message ?? 'Failed to delete offering')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <PageShell id="page-services" isActive={isActive}>
      <div className="content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontFamily: 'Manrope, sans-serif', fontSize: 26, margin: 0, color: 'var(--text)' }}>
              Offerings
            </h1>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>
              Manage what your business offers — services or products. Offerings appear in AI-generated emails when relevant.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-secondary" onClick={() => setShowImportWizard(true)}>
              Import CSV
            </button>
            <button className="btn-primary" onClick={() => setShowAddModal(true)}>
              + Add Offering
            </button>
          </div>
        </div>

        {loading && (
          <div style={{ color: 'var(--text2)', fontSize: 14, padding: '32px 0', textAlign: 'center' }}>
            Loading offerings…
          </div>
        )}

        {!loading && loadError && (
          <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '12px 16px', fontSize: 13 }}>
            {loadError}
          </div>
        )}

        {!loading && !loadError && services.length === 0 && (
          <div
            className="panel"
            style={{ textAlign: 'center', padding: '48px 24px' }}
          >
            <div style={{ fontSize: 36, marginBottom: 12 }}>🗂️</div>
            <div style={{ fontWeight: 600, fontSize: 16, color: 'var(--text)', marginBottom: 8 }}>
              No offerings yet
            </div>
            <div style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 20 }}>
              Add what your business offers — services or products. The AI will reference them naturally in emails.
            </div>
            <button className="btn-primary" onClick={() => setShowAddModal(true)}>
              Add your first offering
            </button>
          </div>
        )}

        {!loading && !loadError && services.length > 0 && (
          <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Name</th>
                  <th style={{ textAlign: 'left' }}>Type</th>
                  <th style={{ textAlign: 'left' }}>Description</th>
                  <th style={{ textAlign: 'right' }}>Price</th>
                  <th style={{ textAlign: 'right' }}>Duration</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.map(svc => (
                  <tr
                    key={svc.id}
                    style={{ opacity: svc.active ? 1 : 0.5 }}
                  >
                    <td>
                      <span style={{ fontWeight: 500, color: 'var(--text)' }}>{svc.name}</span>
                    </td>
                    <td>
                      {(() => {
                        const isProduct = svc.offeringType === 'PRODUCT'
                        return (
                          <span
                            className={`pill ${isProduct ? 'pill-recovered' : 'pill-pending'}`}
                            style={{ fontSize: 11 }}
                          >
                            {isProduct ? 'Product' : 'Service'}
                          </span>
                        )
                      })()}
                    </td>
                    <td style={{ color: 'var(--text2)', fontSize: 13, maxWidth: 240 }}>
                      {svc.description ?? <span style={{ fontStyle: 'italic', color: 'var(--text3)' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                      {svc.price != null
                        ? `$${svc.price.toFixed(2)}`
                        : <span style={{ color: 'var(--text3)' }}>—</span>
                      }
                    </td>
                    <td style={{ textAlign: 'right', fontSize: 13, color: 'var(--text2)' }}>
                      {svc.offeringType === 'PRODUCT'
                        ? <span style={{ color: 'var(--text3)' }}>—</span>
                        : svc.durationMinutes != null
                          ? `${svc.durationMinutes} min`
                          : <span style={{ color: 'var(--text3)' }}>—</span>
                      }
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span
                        className={`pill ${svc.active ? 'pill-pending' : 'pill-lapsed'}`}
                        style={{ fontSize: 11 }}
                      >
                        {svc.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          className="btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => setEditTarget(svc)}
                          disabled={togglingId === svc.id || deletingId === svc.id}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => void handleToggleActive(svc)}
                          disabled={togglingId === svc.id || deletingId === svc.id}
                        >
                          {svc.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="btn-danger"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => void handleDelete(svc)}
                          disabled={togglingId === svc.id || deletingId === svc.id}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <ServiceModal
          mode="add"
          onClose={() => setShowAddModal(false)}
          onSaved={loadServices}
        />
      )}

      {editTarget && (
        <ServiceModal
          mode="edit"
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={loadServices}
        />
      )}

      <ServiceImportWizard
        open={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        onImported={loadServices}
      />
    </PageShell>
  )
}
